// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from 'react';
import { useTranslation } from 'react-i18next';

import './LabelingPanel.scss';
import { updateDoc, query, collection, where, arrayRemove, deleteField, arrayUnion } from 'firebase/firestore';
import Select from 'react-select';
import QualitySliderDropdown from '../QualitySliderDropdown/QualitySliderDropdown.jsx';
import SliderInput from '../Slider/Slider.jsx';
import { useGetCollection } from '../hooks/useGetCollection.js';
import { USERS_COLLECTION_PATH } from '../utils/FirebaseUtils.js';
import { getLabelDisplayName, getLabelsetDisplayName } from '../utils/FirebaseLabelsetsUtils.js';
import { hasWritePermission } from '../utils/WritePermission.js';

export default function LabelingPanel({
  labelItem,
  docRef,
  getNext,
  currentUserUid,
  currentUserInfoFlags,
  currentUserClientId,
  labelsets,
  firestore,
  searchableDropdowns = false,
  showLabelers = false,
  showLabelRequest = false,
}) {
  const { i18n, t } = useTranslation();
  const [selectedLabels, setSelectedLabels] = React.useState({});
  const [labelOptions, setLabelOptions] = React.useState({});
  const [multiLabelOptions, setMultiLabelOptions] = React.useState({});
  const [requestLabel, setRequestLabel] = React.useState(false);

  React.useEffect(() => {
    /* Load labels for the labelItem and build select-dropdowns */
    if (!labelItem) {
      return;
    }

    const selectedLabelsNew = {};
    if (labelItem.labels || labelItem.labels_continuous) {
      Object.keys(labelsets).forEach((labelsetId) => {
        if (labelItem.labels && labelsetId in labelItem.labels && currentUserUid in labelItem.labels[labelsetId]) {
          const labelsetType = labelsets[labelsetId].type;
          if (labelsetType === 'multiple-choice') {
            selectedLabelsNew[labelsetId] = [];
            labelItem.labels[labelsetId][currentUserUid].forEach((id) => {
              if (id)
                selectedLabelsNew[labelsetId].push({
                  value: id,
                  label: getLabelDisplayName(labelsets[labelsetId], id, i18n.language),
                });
            });
          } else {
            const id = labelItem.labels[labelsetId][currentUserUid];
            if (!id) return;
            const value = labelItem.labels[labelsetId][currentUserUid];
            const labelDisplayName = getLabelDisplayName(labelsets[labelsetId], id, i18n.language);

            if (labelsetType === 'single-choice-with-number') {
              const continuous_value =
                labelItem?.labels_continuous?.[labelsetId]?.[currentUserUid] ?? // Use continuous value if available
                labelsets[labelsetId]?.labels?.[id]?.continuous_value; // If not use default value associated with label
              selectedLabelsNew[labelsetId] = { value, label: labelDisplayName, continuous_value };
            } else {
              selectedLabelsNew[labelsetId] = { value, label: labelDisplayName };
            }
          }

          // handle continuous value only labels (e.g slider)
        } else if (
          labelItem.labels_continuous &&
          labelsetId in labelItem.labels_continuous &&
          currentUserUid in labelItem.labels_continuous[labelsetId]
        ) {
          const value = labelItem.labels_continuous[labelsetId][currentUserUid];
          selectedLabelsNew[labelsetId] = { value };
        } else {
          selectedLabelsNew[labelsetId] = [];
        }
      });
    } else {
      Object.keys(labelsets).forEach((id) => {
        if (labelsets[id]?.type === 'multiple-choice') {
          selectedLabelsNew[id] = [];
        } else {
          selectedLabelsNew[id] = '';
        }
      });
    }
    setSelectedLabels(selectedLabelsNew);
    buildLabelOptions();
    setRequestLabel(labelItem?.request_label ?? false);
  }, [labelItem]);

  async function buildLabelOptions() {
    /* Build the options lists for the select-dropdowns. One for each labelset */
    const labelOptionsNew = {};
    const multiLabelOptionsNew = {};

    Object.keys(labelsets).forEach((id) => {
      if (labelsets[id]?.type === 'multiple-choice') {
        multiLabelOptionsNew[id] = [];
        Object.keys(labelsets[id].labels).forEach((labelId) => {
          multiLabelOptionsNew[id].push({
            value: labelId,
            label: getLabelDisplayName(labelsets[id], labelId, i18n.language),
          });
        });
      } else if (labelsets[id]?.type === 'single-choice-with-number') {
        labelOptionsNew[id] = getLabelOptions(id);
        labelOptionsNew[id] = addContinuousValuesToLabels(labelOptionsNew[id], id);
      } else if (labelsets[id]?.type === 'checkbox') {
        labelOptionsNew[id] = Object.keys(labelsets[id].labels)
          .sort()
          .map((labelId) => {
            return { value: labelId, label: getLabelDisplayName(labelsets[id], labelId, i18n.language) };
          });
      } else {
        labelOptionsNew[id] = getLabelOptions(id);
      }
    });

    setLabelOptions(labelOptionsNew);
    setMultiLabelOptions(multiLabelOptionsNew);
  }

  function getLabelOptions(labelsetId) {
    return Object.keys(labelsets[labelsetId].labels)
      .sort()
      .map((labelId) => {
        return { value: labelId, label: getLabelDisplayName(labelsets[labelsetId], labelId, i18n.language) };
      });
  }

  function addContinuousValuesToLabels(labelOptions, labelsetId) {
    /* Add the continuous_value to label options of a labelset of type single-choice-with-number */
    const labelOptionsWithContinuousValues = labelOptions.map((option) => {
      return {
        ...option,
        continuous_value: labelsets?.[labelsetId]?.labels?.[option.value]?.continuous_value ?? 0,
      };
    });
    return labelOptionsWithContinuousValues;
  }

  /* Load all users in the company */
  let usersQuery = collection(firestore, USERS_COLLECTION_PATH);
  // If not admin, you can only load users from within your company
  if (!currentUserInfoFlags.includes('admin')) {
    usersQuery = query(usersQuery, where('company', '==', currentUserClientId));
  }
  const { data: usersData } = useGetCollection(usersQuery, {
    initialCall: !!currentUserInfoFlags.includes('admin'),
  });
  const users = usersData?.reduce((acc, user) => {
    acc[user.id] = user;
    return acc;
  }, {});

  async function onLabelChange(labelsetId, selectedLabel) {
    if (!hasWritePermission()) return;

    function getLabelHistoryEntry(labelsetId, label, labelContinuous = null) {
      const labelHistory = {
        labelset_id: labelsetId,
        user_id: currentUserUid,
        timestamp: new Date(),
        label: label,
        label_continuous: labelContinuous,
      };
      return arrayUnion(labelHistory);
    }

    // Update component state
    setSelectedLabels((prev) => ({
      ...prev,
      [labelsetId]: selectedLabel,
    }));

    const updateDict = {
      labelled: true,
      request_label: false,
      request_label_from_users: arrayRemove(currentUserUid),
    };

    const labelsetType = labelsets[labelsetId]?.type;

    // --- For multiple-choice (where selectedLabel is expected to be an array) ---
    if (labelsetType === 'multiple-choice') {
      if (Array.isArray(selectedLabel) && selectedLabel.length > 0) {
        // Update with the array of selected values
        updateDict[`labels.${labelsetId}.${currentUserUid}`] = selectedLabel.map((option) => option.value);
        updateDict['labeling_history'] = getLabelHistoryEntry(
          labelsetId,
          selectedLabel.map((option) => option.value),
        );
      } else {
        // No selections: delete the field
        updateDict[`labels.${labelsetId}.${currentUserUid}`] = deleteField();
        updateDict['labeling_history'] = getLabelHistoryEntry(labelsetId, null);
      }
    }
    // --- For number-only labelsets ---
    else if (labelsetType === 'number') {
      // If selectedLabel exists and its value is not null or undefined, update the continuous field
      if (selectedLabel && selectedLabel.value !== null && selectedLabel.value !== undefined) {
        updateDict[`labels_continuous.${labelsetId}.${currentUserUid}`] = selectedLabel.value;
        updateDict['labeling_history'] = getLabelHistoryEntry(labelsetId, null, selectedLabel.value);
      } else {
        // … otherwise, clear (delete) the field
        updateDict[`labels_continuous.${labelsetId}.${currentUserUid}`] = deleteField();
        updateDict['labeling_history'] = getLabelHistoryEntry(labelsetId, null, null);
      }
    }
    // --- Handling single-choice labels with an additional slider value ---
    else if (labelsetType === 'single-choice-with-number' || labelsetType === 'number') {
      if (selectedLabel && selectedLabel.value && selectedLabel.value.trim() !== '') {
        // Store the selected categorical label
        updateDict[`labels.${labelsetId}.${currentUserUid}`] = selectedLabel.value;
        // Store the associated continuous value if available
        updateDict[`labels_continuous.${labelsetId}.${currentUserUid}`] = selectedLabel.continuous_value ?? null;
        updateDict['labeling_history'] = getLabelHistoryEntry(
          labelsetId,
          selectedLabel.value,
          selectedLabel.continuous_value,
        );
      } else {
        // If no valid value is selected, clear both the main and continuous fields
        updateDict[`labels.${labelsetId}.${currentUserUid}`] = deleteField();
        updateDict[`labels_continuous.${labelsetId}.${currentUserUid}`] = deleteField();
        updateDict['labeling_history'] = getLabelHistoryEntry(labelsetId, null, null);
      }
    }
    // --- Handling other label types (e.g., single-choice without slider, checkboxes) ---
    else {
      if (selectedLabel && selectedLabel.value && selectedLabel.value.trim() !== '') {
        updateDict[`labels.${labelsetId}.${currentUserUid}`] = selectedLabel.value;
        updateDict['labeling_history'] = getLabelHistoryEntry(labelsetId, selectedLabel.value);
      } else {
        updateDict[`labels.${labelsetId}.${currentUserUid}`] = deleteField();
        updateDict['labeling_history'] = getLabelHistoryEntry(labelsetId, null);
      }
    }

    // Finally, update Firestore if a document reference exists
    if (docRef) {
      updateDoc(docRef, updateDict);
    }
  }

  async function onSkipLabel() {
    if (!hasWritePermission()) return;
    updateDoc(docRef, { labelled: true });
    getNext(labelItem);
  }

  function onRequestLabel() {
    if (!hasWritePermission()) return;
    /* Handle when the checkbox to request labeling for an observation is changed */
    if (docRef) {
      updateDoc(docRef, {
        request_label: !requestLabel,
      });
      setRequestLabel((prev) => !prev);
    }
  }

  return (
    <div className='labeling-dropdowns'>
      {/* Single-Choice Labelling Dropdowns */}
      {Object.keys(labelOptions)
        .sort((a, b) => {
          return labelsets[a]?.sorting_index - labelsets[b]?.sorting_index;
        })
        .map((id) => {
          if (labelOptions[id].length === 0) return;
          return (
            <div key={id}>
              <p className='material-label-title'>{getLabelsetDisplayName(labelsets[id], i18n.language)}:</p>
              {labelsets[id].type === 'single-choice-with-number' ? (
                <QualitySliderDropdown
                  labelOptions={labelOptions[id]}
                  selectedLabel={selectedLabels[id]}
                  onLabelChange={(e) => onLabelChange(id, e)}
                  showDropdown={labelsets[id].show_dropdown ?? true}
                />
              ) : labelsets[id].type === 'number' ? (
                // New slider-only component
                <SliderInput
                  value={selectedLabels[id]?.value || 0}
                  onChangeCommitted={(newValue) => onLabelChange(id, { value: newValue })}
                  min={0}
                  max={1}
                />
              ) : (
                // Fallback to normal dropdown for other types
                <Select
                  isClearable={true}
                  isSearchable={searchableDropdowns}
                  options={labelOptions[id]}
                  value={selectedLabels[id]}
                  onChange={(e) => onLabelChange(id, e)}
                  className='material-label-select'
                />
              )}
              {showLabelers &&
                currentUserInfoFlags.includes('admin') &&
                labelItem.labels &&
                labelItem.labels[id] &&
                Object.keys(labelItem.labels[id]).map((key) => {
                  const labelId = labelItem.labels[id][key];
                  if (!labelId) return;
                  const userName = users[key]?.displayName ?? key;
                  return (
                    <p key={key} className='material-label--annotations'>
                      {getLabelDisplayName(labelsets[id], labelId, i18n.language)} - {userName}
                    </p>
                  );
                })}
            </div>
          );
        })}
      {/* Multiple-Choice Labelling Dropdowns */}
      {Object.keys(multiLabelOptions).map((id) => {
        return (
          <div key={id}>
            <p className='material-label-title'>{getLabelsetDisplayName(labelsets[id], i18n.language)}:</p>
            <Select
              isMulti
              options={multiLabelOptions[id]}
              value={selectedLabels[id]}
              onChange={(e) => {
                onLabelChange(id, e);
              }}
              className='material-label-select'
              styles={{
                option: (base) => ({
                  ...base,
                  padding: '0.2rem 0rem 0.2rem 0.5rem',
                }),
              }}
              isSearchable={searchableDropdowns}
            />

            {showLabelers &&
              currentUserInfoFlags.includes('admin') &&
              labelItem.labels &&
              labelItem.labels[id] &&
              Object.keys(labelItem.labels[id]).map((key, index) => {
                const labelIds = labelItem.labels[id][key];
                if (!labelIds || labelIds.length === 0) return;
                return (
                  <p key={index} className='material-label--annotations'>
                    {labelIds.reduce(
                      (acc, labelId) => `${acc}${getLabelDisplayName(labelsets[id], labelId, i18n.language)}, `,
                      '',
                    )}
                  </p>
                );
              })}
          </div>
        );
      })}
      {/* Skip-button */}
      <button className='material-label--button' onClick={onSkipLabel}>
        {t('labeling_accept')}
      </button>
      {/* Request label checkbox */}
      {showLabelRequest &&
        currentUserInfoFlags &&
        ['admin', 'labeler'].some((flag) => currentUserInfoFlags.includes(flag)) && (
          <div className='request-label-container'>
            <input type='checkbox' checked={requestLabel} onChange={onRequestLabel} />
            <label>{t('labeling_request')}</label>
          </div>
        )}
    </div>
  );
}
